import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {preparationTypeOptions} from '@/modules/drug-catalog/constants'
import {getSubstanceFormDefaultValues} from '@/modules/drug-catalog/substances/functions'
import {useGetSettings} from '@/modules/settings/api'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {Tooltip} from '@cypro/ui/components'
import {FormGrid} from '@cypro/ui/components/ui/form-grid'
import {withForm} from '@cypro/ui/integrations/form'
import {Box} from '@chakra-ui/react'
import {BasicDataResources} from '@cypro/engine/schema'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

export const PropertiesHandlingTab = withForm({
  defaultValues: getSubstanceFormDefaultValues(undefined),
  props: {} as {isEditing?: boolean},
  render: function Render({form, isEditing}) {
    const {t} = useTranslate('common')
    const {t: tSubstances} = useTranslate('substances')

    const {settings} = useGetSettings()

    const hasSaltContent = useStore(form.store, state => state.values.hasSaltContent)

    const {items: hazardClasses} = useGetBasicDataItems({resource: BasicDataResources.hazardClasses})
    const hazardClassesSelectItems = mapBasicDataItemsToSelectItems(hazardClasses)

    const preparationTypeSelectItems = preparationTypeOptions.map(item => ({value: item, label: t(item)}))
    const scalesDisabled = settings?.useScales === false
    const preparationTypeEditDisabled = scalesDisabled && !isEditing

    return (
      <Box spaceY="8">
        <FormSection
          label={tSubstances('physical_properties')}
          description={tSubstances('physical_properties_description')}
        >
          <form.AppField name="hazardClassId">
            {field => (
              <field.Select
                size="sm"
                label={t('hazard_class')}
                placeholder={hazardClassesSelectItems[0]?.label}
                items={hazardClassesSelectItems}
                multiple
                maxW="16rem"
              />
            )}
          </form.AppField>

          <form.AppField name="hasSaltContent">
            {field => <field.Switch size="sm" label={t('has_salt_content')} />}
          </form.AppField>

          {hasSaltContent && (
            <FormGrid gridTemplateColumns="16rem 16rem 16rem">
              <form.AppField name="molarMassFreeBase">
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('molar_mass_free_base')}
                    placeholder={t('eg', {value: 10})}
                    min={0}
                  />
                )}
              </form.AppField>
              <form.AppField name="molarMassSaltBase">
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('molar_mass_salt_base')}
                    placeholder={t('eg', {value: 10})}
                    min={0}
                  />
                )}
              </form.AppField>
              <form.AppField name="saltFactor">
                {field => (
                  <field.NumberInput size="sm" label={t('salt_factor')} placeholder={t('eg', {value: 10})} min={0} />
                )}
              </form.AppField>
            </FormGrid>
          )}
        </FormSection>

        <FormSection
          label={tSubstances('production_handling')}
          description={tSubstances('production_handling_description')}
        >
          <FormGrid>
            <form.AppField name="preparationType">
              {field => (
                <field.Select
                  size="sm"
                  label={
                    <>
                      {t('preparation_type')}

                      {scalesDisabled && isEditing && (
                        <Tooltip content={tSubstances('organization_does_not_use_scales_preparation_not_reflected')} />
                      )}

                      {preparationTypeEditDisabled && (
                        <Tooltip content={tSubstances('organization_does_not_use_scales_preparation_not_editable')} />
                      )}
                    </>
                  }
                  items={preparationTypeSelectItems}
                  placeholder={t('eg', {value: preparationTypeSelectItems?.[0]?.label})}
                  disabled={preparationTypeEditDisabled}
                />
              )}
            </form.AppField>
          </FormGrid>

          <FormGrid>
            <Box gridColumn="span 2">
              <form.AppField name="productionNotes">
                {field => (
                  <field.Textarea
                    size="sm"
                    label={t('production_notes')}
                    rows={4}
                    placeholder={tSubstances('production_note_placeholder')}
                  />
                )}
              </form.AppField>
            </Box>
          </FormGrid>
        </FormSection>
      </Box>
    )
  },
})
